import * as React from "react";
import { ActionBar } from "@origin-digital/action-bar";
import styled from "styled-components";
import { breakPoint } from "@origin-digital/helper";
import { AlertBanner, BREAK_POINTS, Colors } from "@origin-digital/style-guide";
import {
  SharedAppConsumer,
  ISharedAppContext
} from "@origin-digital/shared-app-context";
import { Z_INDEX } from "../../consts/style";
import {
  eventDispatcher,
  IActionBarUpdate,
  IAlertBanner,
  IAlertBannerClose,
  IOriginEvents,
  ISubscription,
  TOPICS
} from "@origin-digital/event-dispatcher";

const ACTION_BAR_HEIGHT = 136;

interface ActionBarProps {
  title?: string;
  mobileNavButton?: "none" | "back" | "close";
  mobilePropertySelector?: "none" | "compact" | "full";
}

interface AlertBannerProps {
  message: string;
  heading?: string;
  type?: "info" | "error" | "success" | "warning";
  uniqueId?: string;
}

interface HeaderProps extends ActionBarProps {
  authenticated?: boolean;
  showActionBar?: boolean;
}

interface HeaderState {
  actionBar: ActionBarProps;
  alertBanner?: AlertBannerProps;

  actionBarUpdateUnsubscribe?: ISubscription;
  alertBannerOpenUnsubscribe?: ISubscription;
  alertBannerCloseUnsubscribe?: ISubscription;
}

const HeaderContainer = styled.div`
  z-index: ${Z_INDEX.HEADER};
  position: sticky;
  top: 0;
  ${breakPoint("LG", BREAK_POINTS.Min.LG)`
    top: -${ACTION_BAR_HEIGHT}px;
    `};
`;

// TODO: move border to originate
const ActionBarContainer = styled.div`
  border-bottom: 1px solid ${Colors.GREY16};
`;

export class Header extends React.Component<HeaderProps, HeaderState> {
  constructor(props: HeaderProps, state: HeaderState) {
    super(props, state);
    const { title, mobileNavButton, mobilePropertySelector } = props;
    this.state = {
      actionBar: { title, mobileNavButton, mobilePropertySelector }
    };
    this.handleOnCloseAlertBanner = this.handleOnCloseAlertBanner.bind(this);
    this.handleOnClickMobileNavButton = this.handleOnClickMobileNavButton.bind(
      this
    );
  }

  componentDidMount() {
    const actionBarUpdateUnsubscribe = eventDispatcher.addListener(
      TOPICS.ACTION_BAR_UPDATE,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ACTION_BAR_UPDATE) {
          this.handleActionBarUpdate(topic);
        }
      }
    );

    const alertBannerOpenUnsubscribe = eventDispatcher.addListener(
      TOPICS.ALERT_BANNER_OPEN,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ALERT_BANNER_OPEN) {
          this.handleAlertBannerOpen(topic);
        }
      }
    );

    const alertBannerCloseUnsubscribe = eventDispatcher.addListener(
      TOPICS.ALERT_BANNER_CLOSE,
      (topic: IOriginEvents) => {
        if (topic.topic === TOPICS.ALERT_BANNER_CLOSE) {
          this.handleAlertBannerClose(topic);
        }
      }
    );

    this.setState({
      actionBarUpdateUnsubscribe,
      alertBannerOpenUnsubscribe,
      alertBannerCloseUnsubscribe
    });
  }

  componentWillUnmount() {
    this.state.actionBarUpdateUnsubscribe &&
      this.state.actionBarUpdateUnsubscribe();
    this.state.alertBannerOpenUnsubscribe &&
      this.state.alertBannerOpenUnsubscribe();
    this.state.alertBannerCloseUnsubscribe &&
      this.state.alertBannerCloseUnsubscribe();
  }

  handleActionBarUpdate(topic: IActionBarUpdate) {
    this.setState({ actionBar: { ...topic.payload } });
  }

  handleAlertBannerOpen(topic: IAlertBanner) {
    this.setState({ alertBanner: { ...topic.payload } });
  }

  handleAlertBannerClose(topic: IAlertBannerClose) {
    if (
      this.state.alertBanner &&
      this.state.alertBanner.uniqueId === topic.payload.uniqueId
    ) {
      this.handleOnCloseAlertBanner();
    }
  }

  handleOnClickMobileNavButton() {
    if (typeof window !== "undefined") {
      window.history.back();
    }
  }

  handleOnCloseAlertBanner() {
    this.setState({ alertBanner: undefined });
  }

  render() {
    const { authenticated, showActionBar } = this.props;

    const { actionBar, alertBanner } = this.state;

    return (
      <SharedAppConsumer>
        {(sac: ISharedAppContext) => (
          <HeaderContainer>
            {showActionBar ? (
              <ActionBarContainer>
                <ActionBar
                  mobilePropertySelector={
                    authenticated ? actionBar.mobilePropertySelector : "none"
                  }
                  mobileNavButton={actionBar.mobileNavButton}
                  onClickMobileNavButton={this.handleOnClickMobileNavButton}
                  title={actionBar.title}
                  onSelectPremise={premiseId => {
                    sac.actions.setPremiseId(premiseId);
                  }}
                  selectedPremiseId={sac.premiseId}
                  premises={sac.properties}
                />
              </ActionBarContainer>
            ) : null}
            {alertBanner ? (
              <AlertBanner
                heading={alertBanner.heading}
                message={alertBanner.message}
                type={alertBanner.type}
                onClose={this.handleOnCloseAlertBanner}
              />
            ) : null}
          </HeaderContainer>
        )}
      </SharedAppConsumer>
    );
  }
}
